/**
 * @techstack/core — Fiscal calendar registry + pure derivation helpers.
 *
 * WHY THIS EXISTS
 * ---------------
 * TekStak charts spend on a fiscal-year axis. Until Aug 2026 that axis was
 * hardcoded to the California State Fiscal Year (Jul 1 → Jun 30) in a dozen
 * places — a `>= 7` month literal in SQL, a `getMonth() >= 6` in React, a
 * `FY_START_MONTH = 6` in the shared picker. That is wrong for the person
 * READING the chart: Ryan sells on the Palo Alto Networks fiscal year
 * (Aug 1 → Jul 31), and "how much did Cisco book in my FY27" is the whole
 * point of the tool.
 *
 * THE BASIS IS THE VIEWER'S CALENDAR, NOT THE VIEWED VENDOR'S.
 * Looking at Cisco spend on the PANW basis is intentional and correct. The
 * basis is NEVER derived from the `vendor` query param.
 *
 * REGISTRY NOW, TABLE LATER
 * -------------------------
 * `FiscalCalendar` is deliberately shaped like a future `fiscal_calendars`
 * table row (one row per tech vendor, refreshed by a cron job, with a
 * per-user default selected in settings). The two rows below are a BOOTSTRAP
 * FALLBACK for first paint / offline — the backend is the runtime authority
 * and serves the same shape from `GET /api/fiscal-calendars`. When the table
 * lands, the backend swaps its seed for a DB read and the UI picks up every
 * new calendar with no frontend change.
 *
 * Everything downstream derives from a row. No new month literals anywhere.
 */

// ─────────────────────────────────────────────────────────────────────────────
// Contract — mirrors the future `fiscal_calendars` table row
// ─────────────────────────────────────────────────────────────────────────────

/**
 * How a fiscal year is named. `end_year` means FY27 is the year ENDING in
 * calendar 2027; `start_year` means FY27 is the year STARTING in 2027
 * (the US federal government uses end_year; some vendors use start_year).
 */
export type FiscalLabelConvention = 'end_year' | 'start_year';

export interface FiscalCalendar {
  /** Stable identifier. Becomes `fiscal_calendars.key` (PK). */
  key: string;
  /** Full display name, e.g. "Palo Alto Networks FY". */
  label: string;
  /** Compact name for a segmented control, e.g. "PANW FY". */
  shortLabel: string;
  /** Month the fiscal year starts, 1-12 (1 = January). */
  fyStartMonth: number;
  /** Day of `fyStartMonth` the fiscal year starts. 1 for every calendar we support. */
  fyStartDay: number;
  /** Whether the FY number is its ending or starting calendar year. */
  labelConvention: FiscalLabelConvention;
  /** Provenance — where the boundary was confirmed. Becomes an audit column. */
  source: string;
  /** ISO date the row was last confirmed. Becomes `updated_at`. */
  updatedAt: string;
}

/** The basis every caller falls back to. Absent/invalid basis MUST land here. */
export const DEFAULT_FISCAL_CALENDAR_KEY = 'ca_state';

/**
 * Bootstrap rows. Keep in step with `apps/backend/lib/fiscalCalendars.js`
 * (the runtime authority) — the backend guard test
 * `tests/lib/fiscal-calendars.test.js` asserts the two agree.
 */
export const FISCAL_CALENDARS: readonly FiscalCalendar[] = Object.freeze([
  Object.freeze({
    key: 'ca_state',
    label: 'California State FY',
    shortLabel: 'State FY',
    fyStartMonth: 7, // July
    fyStartDay: 1,
    // CA SFY 2027 runs 2026-07-01 → 2027-06-30 and is called "FY27" — the
    // ENDING calendar year. This also matches mv_vendor_analysis_facts.sfy_year.
    labelConvention: 'end_year' as FiscalLabelConvention,
    source: 'CA Dept. of Finance — state fiscal year begins July 1',
    updatedAt: '2026-08-24',
  }),
  Object.freeze({
    key: 'panw',
    label: 'Palo Alto Networks FY',
    shortLabel: 'PANW FY',
    fyStartMonth: 8, // August
    fyStartDay: 1,
    // AUTHORITATIVE (Ryan, 2026-08-24): PANW calls Aug 1 2026 – Jul 31 2027
    // "FY27" — the FY number is the ENDING calendar year, the same rule the
    // state uses. The calendars differ ONLY in boundary month (Jul 1/Aug 1),
    // so Jul 1–Jul 31 is the single window where they disagree.
    labelConvention: 'end_year' as FiscalLabelConvention,
    source: 'PANW 10-K — fiscal year ends July 31',
    updatedAt: '2026-08-24',
  }),
]);

// ─────────────────────────────────────────────────────────────────────────────
// Registry access
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Resolve a basis key against a registry. Unknown/absent/invalid → the
 * default (ca_state). This fallback is a CONTRACT: GAS and every legacy
 * caller pass nothing and must keep getting state-basis behavior.
 */
export function getFiscalCalendar(
  key?: string | null,
  registry: readonly FiscalCalendar[] = FISCAL_CALENDARS,
): FiscalCalendar {
  const found = key ? registry.find((c) => c.key === key) : undefined;
  if (found) return found;
  return (
    registry.find((c) => c.key === DEFAULT_FISCAL_CALENDAR_KEY) ?? registry[0]
  );
}

/** Month names used for axis/subtitle copy. Index 1-12. */
const MONTH_ABBR = [
  '', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

/** The calendar month a fiscal year ENDS in (1-12). */
export function fiscalEndMonth(cal: FiscalCalendar): number {
  return ((cal.fyStartMonth + 10) % 12) + 1;
}

/**
 * "Jul–Jun" / "Aug–Jul". Used so a bare "FY27" is never ambiguous.
 */
export function fiscalSpanLabel(cal: FiscalCalendar): string {
  return `${MONTH_ABBR[cal.fyStartMonth]}–${MONTH_ABBR[fiscalEndMonth(cal)]}`;
}

/**
 * Axis / subtitle text that states the basis outright, e.g.
 * "Palo Alto Networks Fiscal Year (Aug–Jul)".
 * NEVER render a bare "FY27" without this nearby.
 */
export function fiscalAxisLabel(cal: FiscalCalendar): string {
  const name = cal.label.replace(/\s*FY$/, '');
  return `${name} Fiscal Year (${fiscalSpanLabel(cal)})`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Pure date math — every boundary comes from the row, never a literal
// ─────────────────────────────────────────────────────────────────────────────

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

function isoOf(year: number, month: number, day: number): string {
  return `${year}-${pad(month)}-${pad(day)}`;
}

/** Last day of a calendar month (month is 1-12). */
function lastDayOfMonth(year: number, month: number): number {
  return new Date(year, month, 0).getDate();
}

/**
 * The fiscal year a date falls in, per the calendar's label convention.
 *
 * `date` may be a Date or a YYYY-MM-DD string. Strings are read as local
 * wall-clock dates on purpose — `new Date('2026-08-01')` is UTC midnight and
 * lands in July for anyone west of Greenwich, which is exactly the off-by-one
 * that makes a boundary PO jump fiscal years.
 */
export function fiscalYearOf(date: Date | string, cal: FiscalCalendar): number {
  const { year, month, day } = partsOf(date);
  const started =
    month > cal.fyStartMonth ||
    (month === cal.fyStartMonth && day >= cal.fyStartDay);
  const startCalendarYear = started ? year : year - 1;
  return cal.labelConvention === 'end_year'
    ? startCalendarYear + 1
    : startCalendarYear;
}

/** The calendar year in which fiscal year `fy` begins. */
export function fiscalStartCalendarYear(fy: number, cal: FiscalCalendar): number {
  return cal.labelConvention === 'end_year' ? fy - 1 : fy;
}

/** Inclusive ISO date range covered by a fiscal year. */
export function fiscalYearRange(
  fy: number,
  cal: FiscalCalendar,
): { start: string; end: string } {
  const sy = fiscalStartCalendarYear(fy, cal);
  const start = isoOf(sy, cal.fyStartMonth, cal.fyStartDay);
  // End = the day before the next FY starts.
  const nextStart = new Date(sy + 1, cal.fyStartMonth - 1, cal.fyStartDay);
  nextStart.setDate(nextStart.getDate() - 1);
  return {
    start,
    end: isoOf(
      nextStart.getFullYear(),
      nextStart.getMonth() + 1,
      nextStart.getDate(),
    ),
  };
}

/**
 * The inverse of `formatFiscalYear`: a rendered FY label back to the date
 * window it covers, on the given calendar.
 *
 * Charts label their bars ("FY24") and a drill-down needs the dates behind
 * the bar that was clicked. Doing that conversion at the call site is how a
 * drill silently ends up on the wrong calendar — the label alone does not say
 * which one the series was bucketed on, so the caller passes the calendar it
 * built the series with.
 *
 * Returns null for anything that is not a well-formed label, so a chart with
 * an unexpected axis makes its bars inert rather than opening a modal onto a
 * window nobody can justify.
 */
export function fiscalYearLabelToRange(
  label: string,
  cal: FiscalCalendar,
): { startDate: string; endDate: string } | null {
  const t = String(label || '').trim();
  // `FY27` is the form every axis renders (Ryan, 2026-09-16: a fiscal year is
  // named by the year it ENDS in, and no hyphens). `FY26-27` is the span form
  // this app used to render in a couple of places; still parsed, because a
  // label can outlive a deploy — in a cached query, a saved drill, an open tab
  // — and a drill-down that silently stops working is worse than a dead branch.
  const m = /^FY(\d{2})(?:-(\d{2}))?$/.exec(t);
  if (!m) return null;
  // Both calendars name a fiscal year by its ENDING calendar year — which is
  // the LAST group present either way.
  const endYear = 2000 + parseInt(m[2] ?? m[1], 10);
  const { start, end } = fiscalYearRange(endYear, cal);
  return { startDate: start, endDate: end };
}

/**
 * Fiscal quarter (1-4) a date falls in. Q1 always starts on the FY start
 * month — so PANW Q1 is Aug–Oct, state Q1 is Jul–Sep. Derived, never listed.
 */
export function fiscalQuarterOf(date: Date | string, cal: FiscalCalendar): number {
  const { month, day } = partsOf(date);
  // Months elapsed since the FY start month, 0-11.
  let elapsed = (month - cal.fyStartMonth + 12) % 12;
  if (month === cal.fyStartMonth && day < cal.fyStartDay) elapsed = 11;
  return Math.floor(elapsed / 3) + 1;
}

/** Inclusive ISO date range covered by a fiscal quarter. */
export function fiscalQuarterRange(
  fy: number,
  quarter: number,
  cal: FiscalCalendar,
): { start: string; end: string } {
  if (!(quarter >= 1 && quarter <= 4)) return fiscalYearRange(fy, cal);
  const sy = fiscalStartCalendarYear(fy, cal);
  const startOffset = (quarter - 1) * 3;
  const startMonthAbs = cal.fyStartMonth - 1 + startOffset; // 0-indexed, may exceed 11
  const startYear = sy + Math.floor(startMonthAbs / 12);
  const startMonth = (startMonthAbs % 12) + 1;

  const endMonthAbs = startMonthAbs + 2;
  const endYear = sy + Math.floor(endMonthAbs / 12);
  const endMonth = (endMonthAbs % 12) + 1;

  // A quarter ends the day before the next quarter's start day. With
  // fyStartDay === 1 (every calendar we support) that is simply the last
  // day of the end month.
  const endDay =
    cal.fyStartDay === 1
      ? lastDayOfMonth(endYear, endMonth)
      : cal.fyStartDay - 1;

  return {
    start: isoOf(startYear, startMonth, cal.fyStartDay),
    end: isoOf(endYear, endMonth, endDay),
  };
}

/** "FY27". Pair with {@link fiscalAxisLabel} so the basis is never ambiguous. */
export function formatFiscalYear(fy: number, _cal?: FiscalCalendar): string {
  return `FY${String(fy).slice(-2)}`;
}

/** "FY27 Q1". */
export function formatFiscalQuarter(
  fy: number,
  quarter: number,
  cal?: FiscalCalendar,
): string {
  return `${formatFiscalYear(fy, cal)} Q${quarter}`;
}

/** The fiscal year that contains `now`. */
export function currentFiscalYear(cal: FiscalCalendar, now: Date = new Date()): number {
  return fiscalYearOf(now, cal);
}

// ─────────────────────────────────────────────────────────────────────────────
// Internals
// ─────────────────────────────────────────────────────────────────────────────

function partsOf(date: Date | string): { year: number; month: number; day: number } {
  if (typeof date === 'string') {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(date);
    if (m) {
      return { year: +m[1], month: +m[2], day: +m[3] };
    }
    const d = new Date(date);
    return { year: d.getFullYear(), month: d.getMonth() + 1, day: d.getDate() };
  }
  return { year: date.getFullYear(), month: date.getMonth() + 1, day: date.getDate() };
}
